/**
 * CurriculumFlow.tsx
 * Three-step curriculum-to-lesson flow:
 *   Step 1 — Source: upload a file, paste a Google URL, or pick from the Hub
 *   Step 2 — Review: edit what TeachBud extracted before generating
 *   Step 3 — Enhance: choose enhancement options then generate
 */
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload, Link2, ArrowLeft, ArrowRight, RefreshCw,
  CheckCircle2, AlertCircle, BookMarked, Library,
  Sparkles, FileText, Eye, Settings2, Info,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  uploadCurriculumFile,
  uploadCurriculumUrl,
  extractCurriculum,
  getCurriculum,
  listCurricula,
  type CurriculumExtract,
  type CurriculumSummary,
} from './curriculum-api';
import type { LessonInputs, CurriculumSource } from './lesson-engine';

// ── Types ─────────────────────────────────────────────────────────────────────

type FlowStep = 'source' | 'uploading' | 'extracting' | 'review' | 'enhance';

interface EnhancementOptions {
  preserveFidelity: boolean;
  improveExplanations: boolean;
  increaseEngagement: boolean;
  addDifferentiation: boolean;
  addMultimedia: boolean;
}

export interface CurriculumFlowProps {
  userId: string;
  onComplete: (inputs: LessonInputs, source: CurriculumSource) => void;
  onBack: () => void;
  /** Pre-select a curriculum from the Hub and skip straight to the Review step */
  initialCurriculumId?: number;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function CurriculumFlow({ userId, onComplete, onBack, initialCurriculumId }: CurriculumFlowProps) {
  const [step, setStep]             = useState<FlowStep>('source');
  const [sourceTab, setSourceTab]   = useState<'upload' | 'hub'>('upload');
  const initialConsumed             = useRef(false);

  // Upload
  const [dragActive, setDragActive] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [urlError, setUrlError]     = useState<string | null>(null);
  const [googleUrl, setGoogleUrl]   = useState('');
  const fileInputRef                = useRef<HTMLInputElement>(null);

  // Curriculum metadata
  const [curriculumId, setCurriculumId]     = useState<number | null>(null);
  const [sourceFileName, setSourceFileName] = useState<string | undefined>();
  const [sourceUrlStored, setSourceUrlStored] = useState<string | undefined>();
  const [sourceType, setSourceType]         = useState('upload');
  const [sourceScope, setSourceScope]       = useState('my');

  // Extraction
  const [extract, setExtract]           = useState<CurriculumExtract | null>(null);
  const [editedExtract, setEditedExtract] = useState<CurriculumExtract | null>(null);
  const [extractError, setExtractError] = useState<string | null>(null);

  // Hub
  const [hubItems, setHubItems]   = useState<CurriculumSummary[] | null>(null);
  const [hubLoading, setHubLoading] = useState(false);

  // Enhancement (step 3)
  const [enhancements, setEnhancements] = useState<EnhancementOptions>({
    preserveFidelity: true,
    improveExplanations: false,
    increaseEngagement: false,
    addDifferentiation: false,
    addMultimedia: false,
  });
  const [duration, setDuration]             = useState('45 minutes');
  const [extraInstructions, setExtraInstructions] = useState('');

  // ── Helpers ──────────────────────────────────────────────────────────────

  async function runExtraction(id: number, force = false) {
    setStep('extracting');
    setExtractError(null);
    try {
      const result = await extractCurriculum(id, userId, force);
      setExtract(result.extract);
      setEditedExtract({ ...result.extract });
      setStep('review');
    } catch (err) {
      setExtractError((err as Error).message ?? 'AI extraction failed. Please try again.');
      setStep('review');
    }
  }

  // ── Pre-load an existing Hub curriculum on mount (from "Use in Lesson") ───
  useEffect(() => {
    if (!initialCurriculumId || initialConsumed.current) return;
    initialConsumed.current = true;

    setSourceTab('hub');
    setCurriculumId(initialCurriculumId);
    setStep('extracting');
    setExtractError(null);

    (async () => {
      try {
        // Get metadata (scope, sourceType, fileName) for the curriculum source record
        const detail = await getCurriculum(initialCurriculumId, userId);
        setSourceScope(detail.scope);
        setSourceType(detail.sourceType);
        setSourceFileName(detail.fileName ?? undefined);
        setSourceUrlStored(detail.sourceUrl ?? undefined);
        // Extract (uses server-side cache if already extracted — no redundant AI call)
        const result = await extractCurriculum(initialCurriculumId, userId);
        setExtract(result.extract);
        setEditedExtract({ ...result.extract });
        setStep('review');
      } catch (err) {
        setExtractError(
          (err as Error).message ?? 'Failed to load curriculum. Please try again.',
        );
        setStep('source');
        setCurriculumId(null);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleFileUpload(file: File) {
    if (file.size > 20 * 1024 * 1024) {
      setUploadError('File is too large. Maximum size is 20 MB.');
      return;
    }
    setUploadError(null);
    setStep('uploading');
    try {
      const result = await uploadCurriculumFile(file, userId);
      setCurriculumId(result.id);
      setSourceFileName(file.name);
      setSourceType('upload');
      await runExtraction(result.id);
    } catch (err) {
      setUploadError((err as Error).message ?? 'Upload failed. Please try again.');
      setStep('source');
    }
  }

  async function handleUrlImport() {
    const url = googleUrl.trim();
    if (!url) { setUrlError('Please enter a URL.'); return; }
    if (!url.startsWith('http')) { setUrlError('Please enter a valid URL starting with http.'); return; }
    setUrlError(null);
    setStep('uploading');
    try {
      const result = await uploadCurriculumUrl(url, userId);
      setCurriculumId(result.id);
      setSourceUrlStored(url);
      setSourceType(
        url.includes('docs.google.com/document') ? 'google_docs' :
        url.includes('docs.google.com/presentation') ? 'google_slides' : 'url'
      );
      await runExtraction(result.id);
    } catch (err) {
      setUrlError((err as Error).message ?? 'Import failed. Please try again.');
      setStep('source');
    }
  }

  async function loadHub() {
    if (hubItems !== null) return;
    setHubLoading(true);
    try {
      const items = await listCurricula(userId, 'my');
      setHubItems(items);
    } catch { setHubItems([]); }
    finally { setHubLoading(false); }
  }

  function handleSelectFromHub(item: CurriculumSummary) {
    setCurriculumId(item.id);
    setSourceScope(item.scope);
    setSourceType(item.sourceType);
    setSourceFileName(item.fileName ?? undefined);
    runExtraction(item.id);
  }

  function setExtractField<K extends keyof CurriculumExtract>(k: K, v: CurriculumExtract[K]) {
    setEditedExtract((prev: CurriculumExtract | null) => prev ? { ...prev, [k]: v } : prev);
  }

  function handleGenerate() {
    if (!editedExtract) return;
    const instrParts: string[] = [];
    if (enhancements.preserveFidelity) instrParts.push('Maintain curriculum fidelity: use the required objective, standards, vocabulary, and instructional sequence from the source curriculum exactly as written. Mark any TeachBud additions with [TeachBud Addition].');
    if (enhancements.improveExplanations) instrParts.push('Improve teacher explanations: add clear modeling steps, worked examples, and think-alouds.');
    if (enhancements.increaseEngagement) instrParts.push('Increase student engagement: add collaborative discussion prompts and hands-on elements where appropriate.');
    if (extraInstructions.trim()) instrParts.push(extraInstructions.trim());

    // Build the Skill to Teach from the curriculum's objective (preferred) or
    // title. Strip any "SWBAT" prefix — the engine re-adds it when composing
    // the lesson objective from the skill.
    const rawSkill = (editedExtract.objective || editedExtract.title || 'understand and apply key concepts')
      .replace(/^students\s+will\s+be\s+able\s+to\s+/i, '')
      .replace(/^students\s+will\s+/i, '')
      .trim();

    const inputs: LessonInputs = {
      skillToTeach: rawSkill,
      grade:        normalizeGrade(editedExtract.grade),
      subject:      normalizeSubject(editedExtract.subject),
      standard:     editedExtract.standards.slice(0, 2).join('; '),
      duration,
      instructions: instrParts.join('\n\n'),
      tieredPractice: enhancements.addDifferentiation,
      multimediaMode: enhancements.addMultimedia ? 'video' : 'none',
    };

    const source: CurriculumSource = {
      id:         curriculumId ?? undefined,
      title:      editedExtract.title,
      publisher:  editedExtract.publisher || undefined,
      gradeLevel: editedExtract.grade || undefined,
      unitInfo:   editedExtract.unitInfo || undefined,
      fileName:   sourceFileName,
      sourceUrl:  sourceUrlStored,
      scope:      sourceScope as CurriculumSource['scope'],
    };

    onComplete(inputs, source);
  }

  // ── Step indicator ────────────────────────────────────────────────────────

  const STEPS = ['Source', 'Extraction', 'Review', 'Enhance'];
  const stepIndex = ({ source: 0, uploading: 1, extracting: 1, review: 2, enhance: 3 } as Record<FlowStep, number>)[step];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
      className="max-w-2xl mx-auto space-y-5">

      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={onBack}
          className="p-2 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-all">
          <ArrowLeft size={18} />
        </button>
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-teal-100 flex items-center justify-center">
            <BookMarked size={18} className="text-teal-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900 leading-tight">Curriculum-Based Lesson</h2>
            <p className="text-xs text-slate-500">Upload your curriculum and TeachBud builds your lesson plan.</p>
          </div>
        </div>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-1.5">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${
              i === stepIndex ? 'bg-teal-500 text-white' :
              i < stepIndex  ? 'bg-teal-100 text-teal-700' : 'bg-slate-100 text-slate-400'
            }`}>
              {i < stepIndex ? <CheckCircle2 size={12} /> : <span className="w-3 text-center">{i + 1}</span>}
              <span className="hidden sm:inline">{s}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`flex-1 h-px ${i < stepIndex ? 'bg-teal-300' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step panels */}
      <AnimatePresence mode="wait">

        {/* ── Step 1: Source ── */}
        {step === 'source' && (
          <motion.div key="source" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5">

            {/* Tabs */}
            <div className="flex gap-1 p-1 bg-slate-100 rounded-xl w-fit">
              {(['upload', 'hub'] as const).map(tab => (
                <button key={tab} onClick={() => { setSourceTab(tab); if (tab === 'hub') loadHub(); }}
                  className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                    sourceTab === tab ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                  }`}>
                  {tab === 'upload' ? 'Upload File' : 'Curriculum Hub'}
                </button>
              ))}
            </div>

            {sourceTab === 'upload' ? (
              <>
                {/* Drop zone */}
                <div
                  onDragOver={e => { e.preventDefault(); setDragActive(true); }}
                  onDragLeave={() => setDragActive(false)}
                  onDrop={e => { e.preventDefault(); setDragActive(false); const f = e.dataTransfer.files[0]; if (f) handleFileUpload(f); }}
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-xl p-8 flex flex-col items-center gap-3 cursor-pointer transition-all ${
                    dragActive ? 'border-teal-400 bg-teal-50' : 'border-slate-200 bg-slate-50/50 hover:border-teal-300 hover:bg-teal-50/30'
                  }`}>
                  <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center">
                    <Upload size={22} className="text-teal-600" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-slate-700">
                      Drag & drop or <span className="text-teal-600">click to upload</span>
                    </p>
                    <p className="text-xs text-slate-400 mt-1">PDF, Word (.docx), PowerPoint (.pptx), images, or plain text · Max 20 MB</p>
                  </div>
                  <input ref={fileInputRef} type="file" className="sr-only"
                    accept=".pdf,.docx,.pptx,.txt,.png,.jpg,.jpeg,.webp"
                    onChange={e => { const f = e.target.files?.[0]; if (f) handleFileUpload(f); }} />
                </div>

                {uploadError && (
                  <p className="text-sm text-red-600 flex items-center gap-1.5"><AlertCircle size={14} /> {uploadError}</p>
                )}

                {/* Divider */}
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-400 font-medium">or import from URL</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>

                {/* Google Docs URL */}
                <div className="space-y-2">
                  <Label className="text-sm font-semibold text-slate-700">Google Docs or Google Slides URL</Label>
                  <div className="flex gap-2">
                    <Input placeholder="https://docs.google.com/document/d/..."
                      value={googleUrl} onChange={e => { setGoogleUrl(e.target.value); setUrlError(null); }}
                      onKeyDown={e => e.key === 'Enter' && handleUrlImport()}
                      className="h-11 rounded-xl border-slate-200 flex-1" />
                    <Button type="button" onClick={handleUrlImport}
                      className="h-11 rounded-xl bg-teal-600 hover:bg-teal-700 text-white px-4 gap-1.5 shrink-0">
                      <Link2 size={15} /> Import
                    </Button>
                  </div>
                  {urlError && (
                    <p className="text-sm text-red-600 flex items-center gap-1.5"><AlertCircle size={14} /> {urlError}</p>
                  )}
                  <p className="text-xs text-slate-400 flex items-start gap-1.5 leading-relaxed">
                    <Info size={12} className="mt-0.5 flex-shrink-0" />
                    Only documents shared as <strong className="text-slate-600">&ldquo;Anyone with the link can view&rdquo;</strong> can be imported. Private or restricted links require Google Drive sign-in (coming soon).
                  </p>
                </div>
              </>
            ) : (
              /* Hub picker */
              <div className="space-y-3">
                {hubLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <svg className="animate-spin w-6 h-6 text-teal-500" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                  </div>
                ) : hubItems && hubItems.length === 0 ? (
                  <div className="flex flex-col items-center gap-3 py-12 text-center">
                    <div className="w-12 h-12 rounded-2xl bg-slate-100 flex items-center justify-center">
                      <Library size={22} className="text-slate-400" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-600 mb-1">No curricula saved yet</p>
                      <p className="text-slate-400 text-sm max-w-xs mx-auto">
                        Upload your first curriculum using the <strong>Upload File</strong> tab.
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {(hubItems ?? []).map(item => (
                      <button key={item.id} onClick={() => handleSelectFromHub(item)}
                        className="w-full flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-teal-300 hover:bg-teal-50/30 transition-all text-left group">
                        <div className="w-9 h-9 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                          <FileText size={16} className="text-teal-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-slate-800 truncate">
                            {item.title || item.fileName || 'Untitled Curriculum'}
                          </p>
                          <p className="text-xs text-slate-400 truncate">
                            {[item.subject, item.gradeLevel, item.publisher].filter(Boolean).join(' · ')}
                          </p>
                        </div>
                        <ArrowRight size={15} className="text-slate-300 group-hover:text-teal-500 flex-shrink-0 transition-colors" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        )}

        {/* ── Uploading / Extracting ── */}
        {(step === 'uploading' || step === 'extracting') && (
          <motion.div key="processing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 flex flex-col items-center gap-5">
            <div className="w-16 h-16 rounded-2xl bg-teal-100 flex items-center justify-center">
              <svg className="animate-spin w-8 h-8 text-teal-500" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-slate-800 mb-1">
                {step === 'uploading' ? 'Uploading and parsing your curriculum…' : 'Reading your curriculum with AI…'}
              </p>
              <p className="text-slate-500 text-sm">
                {step === 'uploading'
                  ? 'Extracting text from your file.'
                  : 'Identifying the objective, standards, vocabulary, and lesson structure.'}
              </p>
            </div>
            {step === 'extracting' && (
              <div className="flex gap-2 flex-wrap justify-center">
                {['Objective', 'Standards', 'Vocabulary', 'Sequence'].map((s, i) => (
                  <motion.span key={s} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.3 }}
                    className="text-xs font-medium bg-teal-100 text-teal-700 rounded-full px-3 py-1">
                    {s}
                  </motion.span>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* ── Step 2: Review ── */}
        {step === 'review' && editedExtract && (
          <motion.div key="review" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5">

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Eye size={16} className="text-teal-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800">Review what TeachBud extracted</p>
                <p className="text-xs text-slate-500 mt-0.5">Edit any field before generating. These values become the foundation for your lesson.</p>
              </div>
            </div>

            {extractError && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex items-center gap-2">
                <AlertCircle size={14} className="flex-shrink-0" />
                {extractError} — You can still edit the fields below manually.
              </div>
            )}

            {/* Fidelity banner */}
            <div className="rounded-xl bg-teal-50 border border-teal-200 p-3 text-xs text-teal-800 font-medium flex items-center gap-2">
              <BookMarked size={13} className="flex-shrink-0 text-teal-600" />
              Curriculum Fidelity — Values extracted from your curriculum are shown below. TeachBud will build the lesson around them.
            </div>

            <div className="space-y-4">
              <ReviewField label="🔒 Lesson Title" value={editedExtract.title}
                onChange={v => setExtractField('title', v)} />

              <div className="grid grid-cols-2 gap-4">
                <ReviewField label="🔒 Subject" value={editedExtract.subject}
                  onChange={v => setExtractField('subject', v)} />
                <ReviewField label="🔒 Grade Level" value={editedExtract.grade}
                  onChange={v => setExtractField('grade', v)} />
              </div>

              <ReviewField label="🔒 Standard(s)" value={editedExtract.standards.join('\n')}
                onChange={v => setExtractField('standards', v.split('\n').filter(Boolean))}
                multiline rows={2} />

              <ReviewField label="🔒 Learning Objective" value={editedExtract.objective}
                onChange={v => setExtractField('objective', v)}
                multiline rows={2} />

              <ReviewField label="🔒 Vocabulary" value={editedExtract.vocabulary.join(', ')}
                onChange={v => setExtractField('vocabulary', v.split(',').map(s => s.trim()).filter(Boolean))} />

              {editedExtract.unitInfo && (
                <ReviewField label="Unit / Module / Chapter" value={editedExtract.unitInfo}
                  onChange={v => setExtractField('unitInfo', v)} />
              )}

              <ReviewField label="Instructional Sequence" value={editedExtract.instructionalSequence}
                onChange={v => setExtractField('instructionalSequence', v)}
                multiline rows={3} />

              <ReviewField label="Activities" value={editedExtract.activities}
                onChange={v => setExtractField('activities', v)}
                multiline rows={2} />

              <ReviewField label="Assessments" value={editedExtract.assessments}
                onChange={v => setExtractField('assessments', v)}
                multiline rows={2} />

              {editedExtract.publisher && (
                <ReviewField label="Publisher / Source" value={editedExtract.publisher}
                  onChange={v => setExtractField('publisher', v)} />
              )}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-slate-100">
              <button
                onClick={() => { if (curriculumId) runExtraction(curriculumId, true); }}
                className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-teal-600 transition-colors">
                <RefreshCw size={14} /> Re-extract
              </button>
              <Button onClick={() => setStep('enhance')}
                className="rounded-xl bg-teal-600 hover:bg-teal-700 text-white gap-1.5">
                Continue <ArrowRight size={15} />
              </Button>
            </div>
          </motion.div>
        )}

        {/* ── Step 3: Enhance ── */}
        {step === 'enhance' && (
          <motion.div key="enhance" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}
            className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5">

            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                <Settings2 size={16} className="text-violet-600" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-800">Enhancement Options</p>
                <p className="text-xs text-slate-500 mt-0.5">Choose how TeachBud should enhance your curriculum.</p>
              </div>
            </div>

            {/* Curriculum summary strip */}
            {editedExtract && (
              <div className="rounded-xl bg-teal-50 border border-teal-200 px-4 py-3 space-y-0.5">
                <p className="text-sm font-bold text-teal-900">{editedExtract.title || 'Curriculum-Based Lesson'}</p>
                <p className="text-xs text-teal-600">
                  {[editedExtract.subject, editedExtract.grade, editedExtract.publisher].filter(Boolean).join(' · ')}
                </p>
              </div>
            )}

            {/* Enhancement checkboxes */}
            <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">How should TeachBud enhance this curriculum?</p>

              {([
                { key: 'preserveFidelity',    label: 'Preserve Curriculum Fidelity',   desc: 'Use the required objective, standards, vocabulary, and sequence exactly as written. Mark TeachBud additions clearly.', icon: '🔒' },
                { key: 'improveExplanations', label: 'Improve Teacher Explanations',   desc: 'Add clear modeling steps, worked examples, and think-alouds.',                                                             icon: '💬' },
                { key: 'increaseEngagement',  label: 'Increase Student Engagement',    desc: 'Add discussion prompts, collaborative tasks, and hands-on elements.',                                                      icon: '⚡' },
                { key: 'addDifferentiation',  label: 'Add Differentiation',            desc: 'Generate Below-Level, On-Level, and Above-Level versions of independent practice.',                                       icon: '📊' },
                { key: 'addMultimedia',       label: 'Add Multimedia Resources',       desc: 'Recommend topic-specific videos and interactive simulations.',                                                             icon: '🎬' },
              ] as const).map(opt => (
                <label key={opt.key} className="flex items-start gap-3 cursor-pointer">
                  <div className="relative mt-0.5 flex-shrink-0">
                    <input type="checkbox" className="sr-only peer"
                      checked={enhancements[opt.key]}
                      onChange={e => setEnhancements(prev => ({ ...prev, [opt.key]: e.target.checked }))} />
                    <div className="w-4 h-4 rounded border-2 border-slate-300 peer-checked:border-teal-500 peer-checked:bg-teal-500 transition-all flex items-center justify-center">
                      {enhancements[opt.key] && <CheckCircle2 size={10} className="text-white" />}
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{opt.icon} {opt.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5">{opt.desc}</p>
                  </div>
                </label>
              ))}
            </div>

            {/* Duration */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">Lesson Duration</Label>
              <Select value={duration} onValueChange={setDuration}>
                <SelectTrigger className="h-11 rounded-xl border-slate-200 w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['30 minutes', '45 minutes', '60 minutes', '90 minutes'].map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Extra instructions */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold text-slate-700">
                Additional Instructions <span className="text-slate-400 font-normal">(optional)</span>
              </Label>
              <Textarea
                placeholder="e.g. My class has 28 students with 6 ELLs. Include sentence frames."
                value={extraInstructions}
                onChange={e => setExtraInstructions(e.target.value)}
                rows={3}
                className="bg-white rounded-xl border-slate-200 resize-none" />
            </div>

            <div className="flex items-center justify-between pt-1 border-t border-slate-100">
              <button onClick={() => setStep('review')}
                className="flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
                <ArrowLeft size={14} /> Back
              </button>
              <Button onClick={handleGenerate}
                className="rounded-xl bg-primary hover:bg-primary/90 text-white gap-1.5 px-6">
                <Sparkles size={15} /> Generate Lesson Plan
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ReviewField({
  label, value, onChange, multiline = false, rows = 1,
}: { label: string; value: string; onChange: (v: string) => void; multiline?: boolean; rows?: number }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold text-teal-700 uppercase tracking-wide">{label}</Label>
      {multiline ? (
        <Textarea value={value} onChange={e => onChange(e.target.value)} rows={rows}
          className="bg-white rounded-xl border-slate-200 text-sm resize-none focus-visible:ring-teal-400/30 focus-visible:border-teal-400" />
      ) : (
        <Input value={value} onChange={e => onChange(e.target.value)}
          className="h-10 bg-white rounded-xl border-slate-200 text-sm focus-visible:ring-teal-400/30 focus-visible:border-teal-400" />
      )}
    </div>
  );
}

// ── Normalizers ───────────────────────────────────────────────────────────────

function normalizeGrade(grade: string): string {
  if (!grade) return '';
  if (/kindergarten/i.test(grade)) return 'K';
  const m = grade.match(/\d+/);
  if (m) { const n = parseInt(m[0], 10); if (n >= 1 && n <= 12) return String(n); }
  return '';
}

function normalizeSubject(subject: string): string {
  const s = subject.toLowerCase();
  if (s.includes('math')) return 'Math';
  if (s.includes('english') || s.includes('ela') || s.includes('language arts') || s.includes('reading') || s.includes('writing')) return 'English Language Arts';
  if (s.includes('science')) return 'Science';
  if (s.includes('social studies') || s.includes('history') || s.includes('geography') || s.includes('civics')) return 'Social Studies';
  return subject || 'Other';
}
